import { useTimeZone } from '@/auth/useTimeZone';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getCoachSessions, requestSubstitutionAsCoach } from '@/lib/api-calls';
import { formatSubRequestDateTime } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';

interface RequestSessionSubstituteProps {
  sessionId: string;
  startInstant: Temporal.Instant;
  endInstant: Temporal.Instant;
  trigger: ReactNode;
  disabled?: boolean;
}

export const RequestSessionSubstitute = ({
  sessionId,
  startInstant,
  endInstant,
  trigger,
  disabled,
}: RequestSessionSubstituteProps) => {
  const { toast } = useToast();
  const { timeZone } = useTimeZone();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending } = useMutation({
    mutationFn: requestSubstitutionAsCoach,
  });

  const { sessionDateFormatted, sessionTimeFormatted } = useMemo(
    () => formatSubRequestDateTime(startInstant, endInstant, timeZone),
    [startInstant, endInstant, timeZone],
  );

  const sessionDateTime = `${sessionDateFormatted}, ${sessionTimeFormatted}`;

  const onSubmit = () => {
    return mutateAsync({
      sessionId,
    })
      .then(() => {
        toast({
          title: `Your sub request has been submitted. The session on ${sessionDateFormatted}, ${sessionTimeFormatted} has been removed from your calendar.`,
        });
        setOpen(false);
        queryClient.invalidateQueries(getCoachSessions());
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger disabled={disabled}>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Request a substitute</AlertDialogTitle>
          <AlertDialogDescription>
            Submit a substitute request for {sessionDateTime}? This will remove it from your calendar.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              Request
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
